import { useCallback, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { useItemsMaster } from '../../api/masterTables';
import {
  useSuppliers,
  useCreateSupplier,
  useUpdateSupplier,
  useDeleteSupplier,
  useSupplierItems,
  useAddSupplierPrice,
  useDeleteSupplierItemPrices,
  useImportSupplierPricesXlsx,
  useSupplierPriceHistory,
  todayIsoDate,
  formatSupplierDate,
  isSupplierItemUnmatched,
  type AddSupplierPricePayload,
  type SupplierRow,
  type SupplierItemRow,
  type SupplierTableRow,
} from '../../api/suppliers';
import { downloadAuthorized } from '../../api/client';
import { ItemEditorForm } from '../items/ItemEditorForm';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { DataTable } from '../ui/DataTable';
import { DropZone } from '../ui/DropZone';
import { Modal } from '../ui/Modal';
import { ConfirmModal } from '../ui/ConfirmModal';
import { useToast } from '../../hooks/useToast';
import page from '../../pages/Page.module.css';
import styles from './SuppliersPanel.module.css';

type ItemRow = Record<string, unknown>;

function IconTemplateDownload() {
  return (
    <svg
      width={18}
      height={18}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={styles.templateDownloadIcon}
      aria-hidden
    >
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M14 2v6h6" />
      <path d="M12 11v8" />
      <path d="m9 16 3 3 3-3" />
    </svg>
  );
}

function IconExportExcel() {
  return (
    <svg
      width={18}
      height={18}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={styles.exportExcelIcon}
      aria-hidden
    >
      <rect x="3" y="3" width="11" height="10" rx="1" />
      <path d="M3 7h11M3 10h11M7 3v10M10 3v10" />
      <path d="M17 11v8M14 16l3 3 3-3" />
    </svg>
  );
}

function ItemEditorModal({
  open,
  onClose,
  title,
  item,
  initialName,
  onSuccess,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  item?: ItemRow;
  initialName?: string;
  onSuccess?: () => void;
}) {
  return (
    <Modal open={open} onClose={onClose} title={title} showClose={false}>
      {open ? (
        <ItemEditorForm
          key={item ? `edit-${String(item.id)}` : `add-${initialName ?? ''}`}
          item={item}
          defaultName={initialName}
          onClose={onClose}
          onSuccess={onSuccess}
        />
      ) : null}
    </Modal>
  );
}

export function SuppliersPanel({ isAdmin }: { isAdmin: boolean }) {
  const toast = useToast();
  const { data: suppliers, isLoading } = useSuppliers();
  const create = useCreateSupplier();
  const update = useUpdateSupplier();
  const del = useDeleteSupplier();
  const [name, setName] = useState('');
  const [editing, setEditing] = useState<SupplierRow | null>(null);
  const [pendingDelete, setPendingDelete] = useState<SupplierRow | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const selected = suppliers?.find((s) => s.id === selectedId) ?? null;

  return (
    <div className={page.card}>
      <h3 className={page.title} style={{ fontSize: 16 }}>
        Suppliers
      </h3>
      <p className={page.muted} style={{ marginBottom: 16 }}>
        Select a supplier to manage item pricing with effective dates. Items must exist in the item master before a
        price can be added.
      </p>

      <div className={page.row} style={{ marginBottom: 16 }}>
        <div className={styles.supplierSelectWrap}>
          <Select
            label="Supplier"
            value={selectedId != null ? String(selectedId) : ''}
            onChange={(e) => setSelectedId(e.target.value ? Number(e.target.value) : null)}
            disabled={isLoading}
          >
            <option value="">{isLoading ? 'Loading…' : 'Select a supplier…'}</option>
            {(suppliers ?? []).map((s) => (
              <option key={s.id} value={String(s.id)}>
                {s.name}
                {Number(s.is_active) === 0 ? ' (Inactive)' : ''}
                {s.item_count != null ? ` — ${s.item_count} items` : ''}
              </option>
            ))}
          </Select>
        </div>
        {isAdmin && selected ? (
          <>
            <Button type="button" variant="ghost" onClick={() => setEditing(selected)}>
              Edit Supplier
            </Button>
            <Button type="button" variant="ghost" onClick={() => setPendingDelete(selected)}>
              Delete Supplier
            </Button>
          </>
        ) : null}
      </div>

      {!isLoading && !suppliers?.length ? <p className={page.muted}>No suppliers yet.</p> : null}

      {isAdmin ? (
        <form
          style={{ marginBottom: 16 }}
          onSubmit={(e) => {
            e.preventDefault();
            if (!name.trim()) return;
            create.mutate(
              { name: name.trim() },
              {
                onSuccess: (result) => {
                  toast('Supplier added');
                  setName('');
                  setSelectedId(result.id);
                },
                onError: (err) => toast(err instanceof Error ? err.message : 'Failed', true),
              }
            );
          }}
        >
          <div className={page.row} style={{ flexWrap: 'wrap' }}>
            <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} required />
            <Button type="submit" variant="success" disabled={create.isPending}>
              Add Supplier
            </Button>
          </div>
        </form>
      ) : null}

      {selected ? (
        <SupplierItemsPanel key={selected.id} supplier={selected} isAdmin={isAdmin} />
      ) : null}

      <Modal open={!!editing} onClose={() => setEditing(null)} title="Edit Supplier" showClose={false}>
        {editing ? <SupplierEditForm row={editing} onClose={() => setEditing(null)} update={update} /> : null}
      </Modal>

      <ConfirmModal
        open={pendingDelete !== null}
        title="Delete Supplier?"
        confirmLabel="Delete"
        onCancel={() => setPendingDelete(null)}
        onConfirm={() => {
          if (!pendingDelete) return;
          del.mutate(pendingDelete.id, {
            onSuccess: () => {
              toast('Supplier deleted');
              if (selectedId === pendingDelete.id) setSelectedId(null);
              setPendingDelete(null);
            },
            onError: (err) => toast(err instanceof Error ? err.message : 'Failed', true),
          });
        }}
        isPending={del.isPending}
      >
        <p style={{ margin: 0 }}>
          Deactivate supplier &quot;{pendingDelete?.name ?? ''}&quot;? Communities using this supplier must be
          reassigned first.
        </p>
      </ConfirmModal>
    </div>
  );
}

function SupplierEditForm({
  row,
  onClose,
  update,
}: {
  row: SupplierRow;
  onClose: () => void;
  update: ReturnType<typeof useUpdateSupplier>;
}) {
  const toast = useToast();
  const [name, setName] = useState(row.name);
  const [active, setActive] = useState(Number(row.is_active) === 1);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!name.trim()) return;
        update.mutate(
          { id: row.id, body: { name: name.trim(), is_active: active } },
          {
            onSuccess: () => {
              toast('Supplier saved');
              onClose();
            },
            onError: (err) => toast(err instanceof Error ? err.message : 'Failed', true),
          }
        );
      }}
    >
      <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} required />
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12 }}>
        <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
        Active
      </label>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
        <Button type="button" variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" variant="success" disabled={update.isPending}>
          Save
        </Button>
      </div>
    </form>
  );
}

function SupplierEditPriceForm({
  item,
  onClose,
  onSave,
  isPending,
}: {
  item: SupplierItemRow;
  onClose: () => void;
  onSave: (payload: { itemId: number; price: number; effective_date: string }) => void;
  isPending: boolean;
}) {
  const toast = useToast();
  const [price, setPrice] = useState(item.price != null ? String(item.price) : '');
  const [effectiveDate, setEffectiveDate] = useState(todayIsoDate());

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        const priceNum = Number(price);
        if (!Number.isFinite(priceNum)) {
          toast('Price required', true);
          return;
        }
        onSave({ itemId: item.item_id, price: priceNum, effective_date: effectiveDate });
      }}
    >
      <p className={page.muted} style={{ marginTop: 0 }}>
        Item: {item.item_name}
      </p>
      <Input
        label="Price"
        type="number"
        step="0.01"
        value={price}
        onChange={(e) => setPrice(e.target.value)}
        required
      />
      <Input
        label="Effective Date"
        type="date"
        value={effectiveDate}
        onChange={(e) => setEffectiveDate(e.target.value)}
        required
      />
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
        <Button type="button" variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" variant="success" disabled={isPending}>
          Save Price
        </Button>
      </div>
    </form>
  );
}

function SupplierItemsPanel({
  supplier,
  isAdmin,
}: {
  supplier: SupplierRow;
  isAdmin: boolean;
}) {
  const toast = useToast();
  const qc = useQueryClient();
  const { data: items, isLoading } = useSupplierItems(supplier.id);
  const { data: itemsMaster } = useItemsMaster();
  const addPrice = useAddSupplierPrice(supplier.id);
  const deleteItemPrices = useDeleteSupplierItemPrices(supplier.id);
  const importPrices = useImportSupplierPricesXlsx(supplier.id);
  const [itemName, setItemName] = useState('');
  const [price, setPrice] = useState('');
  const [effectiveDate, setEffectiveDate] = useState(todayIsoDate());
  const [unmatchedItem, setUnmatchedItem] = useState<string | null>(null);
  const [pendingPrice, setPendingPrice] = useState<{
    itemName: string;
    price: number;
    effective_date: string;
  } | null>(null);
  const [historyItem, setHistoryItem] = useState<SupplierItemRow | null>(null);
  const [editingItem, setEditingItem] = useState<SupplierItemRow | null>(null);
  const [importUnmatched, setImportUnmatched] = useState<string[]>([]);
  const [masterItemModal, setMasterItemModal] = useState<
    { mode: 'add'; name: string } | { mode: 'edit'; item: ItemRow } | null
  >(null);
  const [pendingDelete, setPendingDelete] = useState<
    { pendingImport: true; item_name: string } | { item_id: number; item_name: string } | null
  >(null);

  const { data: priceHistory, isLoading: historyLoading } = useSupplierPriceHistory(
    supplier.id,
    historyItem?.item_id ?? null
  );

  async function submitPrice(payload: AddSupplierPricePayload) {
    setUnmatchedItem(null);
    try {
      await addPrice.mutateAsync(payload);
      toast('Price saved');
      setItemName('');
      setPrice('');
      setEffectiveDate(todayIsoDate());
      setPendingPrice(null);
      setEditingItem(null);
    } catch (err) {
      const unmatched = (err as Error & { unmatchedItem?: string }).unmatchedItem;
      if (unmatched) {
        setUnmatchedItem(unmatched);
        setPendingPrice(
          payload.itemName
            ? {
                itemName: payload.itemName,
                price: payload.price,
                effective_date: payload.effective_date ?? todayIsoDate(),
              }
            : null
        );
        toast(`"${unmatched}" is not in the item master`, true);
        return;
      }
      toast(err instanceof Error ? err.message : 'Failed to save price', true);
    }
  }

  async function runPricesImport(file: File) {
    setImportUnmatched([]);
    try {
      const result = await importPrices.mutateAsync(file);
      const parts = [`Imported ${result.imported} price${result.imported === 1 ? '' : 's'}`];
      if (result.skipped > 0) parts.push(`${result.skipped} row${result.skipped === 1 ? '' : 's'} skipped`);
      toast(parts.join('; '));
      if (result.unmatched.length) {
        setImportUnmatched(result.unmatched);
        toast(
          `${result.unmatched.length} item${result.unmatched.length === 1 ? '' : 's'} not in the item master`,
          true
        );
      }
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Import failed', true);
    }
  }

  const tableRows = useMemo<SupplierTableRow[]>(() => {
    const pending: SupplierTableRow[] = importUnmatched.map((name) => ({
      item_id: 0,
      item_name: name,
      price: null,
      effective_date: '',
      in_item_master: 0,
      pendingImport: true,
    }));
    const priced = (items ?? []).map((row) => ({ ...row, pendingImport: false as const }));
    return [...pending, ...priced];
  }, [items, importUnmatched]);

  const openMasterItemModal = useCallback(
    (row: SupplierTableRow) => {
      if (row.pendingImport || !row.item_id) {
        setMasterItemModal({ mode: 'add', name: row.item_name });
        return;
      }
      const master = itemsMaster?.find((i) => Number(i.id) === row.item_id);
      if (master) {
        setMasterItemModal({ mode: 'edit', item: master as ItemRow });
        return;
      }
      setMasterItemModal({ mode: 'add', name: row.item_name });
    },
    [itemsMaster]
  );

  function onMasterItemSaved() {
    const modal = masterItemModal;
    setMasterItemModal(null);
    void qc.invalidateQueries({ queryKey: ['suppliers', supplier.id, 'items'] });
    if (modal?.mode === 'add') {
      setImportUnmatched((prev) => prev.filter((n) => n !== modal.name));
      setUnmatchedItem((current) => (current === modal.name ? null : current));
    }
    if (pendingPrice && modal?.mode === 'add' && pendingPrice.itemName === modal.name) {
      void submitPrice(pendingPrice);
    }
  }

  function confirmDeleteItem() {
    if (!pendingDelete) return;
    if ('pendingImport' in pendingDelete) {
      setImportUnmatched((prev) => prev.filter((n) => n !== pendingDelete.item_name));
      setPendingDelete(null);
      toast('Item removed');
      return;
    }
    deleteItemPrices.mutate(pendingDelete.item_id, {
      onSuccess: () => {
        toast('Item removed from supplier price list');
        if (historyItem?.item_id === pendingDelete.item_id) setHistoryItem(null);
        if (editingItem?.item_id === pendingDelete.item_id) setEditingItem(null);
        setPendingDelete(null);
      },
      onError: (err) => toast(err instanceof Error ? err.message : 'Failed to delete', true),
    });
  }

  const itemCols = useMemo<ColumnDef<SupplierTableRow>[]>(
    () => [
      {
        accessorKey: 'item_name',
        header: 'Item',
        cell: ({ row }) => (
          <span>
            {row.original.item_name}
            {isSupplierItemUnmatched(row.original) ? (
              <span className={styles.supplierUnmatchedBadge}>Not in Item Master</span>
            ) : null}
          </span>
        ),
      },
      {
        id: 'price',
        header: 'Current Price',
        cell: ({ row }) =>
          row.original.pendingImport
            ? '—'
            : row.original.price != null
              ? Number(row.original.price).toLocaleString(undefined, { minimumFractionDigits: 2 })
              : '—',
      },
      {
        id: 'effective_date',
        header: 'Effective Date',
        cell: ({ row }) =>
          row.original.pendingImport ? '—' : formatSupplierDate(row.original.effective_date),
      },
      {
        id: 'updated',
        header: 'Updated',
        cell: ({ row }) =>
          row.original.pendingImport ? '—' : formatSupplierDate(row.original.updated_at),
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {isAdmin && isSupplierItemUnmatched(row.original) ? (
              <Button
                type="button"
                variant="success"
                size="sm"
                onClick={() => openMasterItemModal(row.original)}
              >
                {row.original.pendingImport || !row.original.item_id
                  ? 'Add to Item Master'
                  : 'Edit Item Master'}
              </Button>
            ) : null}
            {isAdmin && !row.original.pendingImport ? (
              <Button type="button" variant="ghost" size="sm" onClick={() => setEditingItem(row.original)}>
                Edit Price
              </Button>
            ) : null}
            {!row.original.pendingImport ? (
              <Button type="button" variant="ghost" size="sm" onClick={() => setHistoryItem(row.original)}>
                Price History
              </Button>
            ) : null}
            {isAdmin ? (
              <Button
                type="button"
                variant="danger"
                size="sm"
                onClick={() =>
                  setPendingDelete(
                    row.original.pendingImport
                      ? { pendingImport: true, item_name: row.original.item_name }
                      : { item_id: row.original.item_id, item_name: row.original.item_name }
                  )
                }
              >
                Delete
              </Button>
            ) : null}
          </div>
        ),
      },
    ],
    [isAdmin, openMasterItemModal]
  );

  return (
    <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--color-border, #e0e0e0)' }}>
      <div style={{ marginBottom: 16 }}>
        <h3 className={page.title} style={{ fontSize: 16, marginBottom: 4 }}>
          Items &amp; Pricing
        </h3>
        <p className={page.muted} style={{ margin: 0 }}>
          Current prices use the latest effective date on or before today. Rows highlighted in red are not in the
          item master — add them before they can be used in bids.
        </p>
      </div>

      <div className={styles.exportToolbar} style={{ marginTop: 16 }}>
        <Button
          type="button"
          variant="ghost"
          onClick={() =>
            void downloadAuthorized(`/api/suppliers/${supplier.id}/export/prices-xlsx`).catch((e) =>
              toast(e instanceof Error ? e.message : 'Export failed', true)
            )
          }
        >
          <IconExportExcel />
          Export to Excel
        </Button>
        {isAdmin ? (
          <Button
            type="button"
            variant="ghost"
            onClick={() =>
              void downloadAuthorized(`/api/suppliers/${supplier.id}/template/prices-xlsx`).catch((e) =>
                toast(e instanceof Error ? e.message : 'Download failed', true)
              )
            }
          >
            <IconTemplateDownload />
            Prices Excel Template
          </Button>
        ) : null}
      </div>

      {isAdmin ? (
        <div style={{ marginTop: 8 }}>
          <DropZone
            accept=".xlsx,.xls"
            onFiles={(files) => files[0] && void runPricesImport(files[0])}
          >
            <h3 style={{ margin: '0 0 8px', fontSize: 16 }}>Drop Excel Here</h3>
            <p className={page.muted} style={{ margin: 0, fontSize: 13 }}>
              Upload prices for {supplier.name}. Columns: Item Name, Price, Effective Date. Items must exist in
              the item master. Rows with the same effective date overwrite the existing price. Use Export to Excel
              or the template for the expected file layout.
            </p>
          </DropZone>
        </div>
      ) : null}

      {importUnmatched.length ? (
        <p className={page.muted} style={{ marginTop: 12, marginBottom: 0 }}>
          {importUnmatched.length} imported item{importUnmatched.length === 1 ? '' : 's'} need to be added to the item
          master (shown at the top of the table).
        </p>
      ) : null}

      {unmatchedItem ? (
        <div
          role="alert"
          style={{
            marginTop: 16,
            padding: 12,
            borderRadius: 8,
            border: '1px solid var(--color-danger, #c0392b)',
            background: 'color-mix(in srgb, var(--color-danger, #c0392b) 8%, transparent)',
          }}
        >
          <p style={{ margin: '0 0 8px', fontWeight: 600 }}>
            Item &quot;{unmatchedItem}&quot; Is Not in the Item Master
          </p>
          <p className={page.muted} style={{ margin: '0 0 12px' }}>
            Add this item to the item master, then retry saving the supplier price.
          </p>
          {isAdmin ? (
            <Button
              type="button"
              variant="success"
              onClick={() => setMasterItemModal({ mode: 'add', name: unmatchedItem })}
            >
              Add to Item Master
            </Button>
          ) : null}
        </div>
      ) : null}

      {isAdmin ? (
        <form
          style={{ marginTop: 16 }}
          onSubmit={(e) => {
            e.preventDefault();
            const trimmed = itemName.trim();
            const priceNum = Number(price);
            if (!trimmed || !Number.isFinite(priceNum)) {
              toast('Item and price required', true);
              return;
            }
            void submitPrice({ itemName: trimmed, price: priceNum, effective_date: effectiveDate });
          }}
        >
          <div className={page.row} style={{ flexWrap: 'wrap' }}>
            <Input label="Item" value={itemName} onChange={(e) => setItemName(e.target.value)} required />
            <Input
              label="Price"
              type="number"
              step="0.01"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              required
            />
            <Input
              label="Effective Date"
              type="date"
              value={effectiveDate}
              onChange={(e) => setEffectiveDate(e.target.value)}
              required
            />
            <Button type="submit" variant="success" disabled={addPrice.isPending}>
              Add Price
            </Button>
          </div>
        </form>
      ) : null}

      {isLoading ? (
        <p className={page.muted} style={{ marginTop: 16 }}>
          Loading items…
        </p>
      ) : null}
      {!isLoading && tableRows.length ? (
        <div style={{ marginTop: 16 }}>
          <DataTable
            data={tableRows}
            columns={itemCols}
            enableSorting
            getRowClassName={(row) =>
              isSupplierItemUnmatched(row as SupplierTableRow) ? styles.supplierUnmatchedRow : undefined
            }
          />
        </div>
      ) : null}
      {!isLoading && !tableRows.length ? (
        <p className={page.muted} style={{ marginTop: 16 }}>
          No priced items for this supplier yet.
        </p>
      ) : null}

      <Modal
        open={!!editingItem}
        onClose={() => setEditingItem(null)}
        title={editingItem ? `Edit Price — ${editingItem.item_name}` : 'Edit Price'}
        showClose={false}
      >
        {editingItem ? (
          <SupplierEditPriceForm
            key={editingItem.item_id}
            item={editingItem}
            onClose={() => setEditingItem(null)}
            isPending={addPrice.isPending}
            onSave={(payload) => {
              void submitPrice(payload);
            }}
          />
        ) : null}
      </Modal>

      <Modal
        open={!!historyItem}
        onClose={() => setHistoryItem(null)}
        title={historyItem ? `Price History — ${historyItem.item_name}` : 'Price History'}
        showClose={false}
      >
        {historyLoading ? <p className={page.muted}>Loading…</p> : null}
        {!historyLoading && priceHistory?.length ? (
          <table className={styles.phasesTable} style={{ width: '100%' }}>
            <thead>
              <tr>
                <th>Effective Date</th>
                <th>Price</th>
                <th>Updated</th>
              </tr>
            </thead>
            <tbody>
              {priceHistory.map((row) => (
                <tr key={row.id}>
                  <td>{formatSupplierDate(row.effective_date)}</td>
                  <td>
                    {row.price != null
                      ? Number(row.price).toLocaleString(undefined, { minimumFractionDigits: 2 })
                      : '—'}
                  </td>
                  <td>{formatSupplierDate(row.updated_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
        {!historyLoading && !priceHistory?.length ? (
          <p className={page.muted}>No price history for this item.</p>
        ) : null}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
          <Button type="button" variant="ghost" onClick={() => setHistoryItem(null)}>
            Close
          </Button>
        </div>
      </Modal>

      <ItemEditorModal
        open={masterItemModal !== null}
        onClose={() => setMasterItemModal(null)}
        title={masterItemModal?.mode === 'edit' ? 'Edit Item Master' : 'Add Item to Master'}
        item={masterItemModal?.mode === 'edit' ? masterItemModal.item : undefined}
        initialName={masterItemModal?.mode === 'add' ? masterItemModal.name : undefined}
        onSuccess={onMasterItemSaved}
      />

      <ConfirmModal
        open={pendingDelete !== null}
        title="Delete Item?"
        confirmLabel="Delete"
        onCancel={() => setPendingDelete(null)}
        onConfirm={confirmDeleteItem}
        isPending={deleteItemPrices.isPending}
      >
        <p style={{ margin: 0 }}>
          {pendingDelete && 'pendingImport' in pendingDelete ? (
            <>Remove &quot;{pendingDelete.item_name}&quot; from the unmatched import list?</>
          ) : (
            <>
              Remove &quot;{pendingDelete?.item_name ?? ''}&quot; from {supplier.name}&apos;s price list? All
              price history for this item will be deleted.
            </>
          )}
        </p>
      </ConfirmModal>
    </div>
  );
}
